import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import uniqueId from 'lodash/uniqueId'
import { PresetTimeRange } from 'ui/Analytics/PresetTimeRange'
import { initialResponseState } from '../fetchingLogic'
import { ACTIVITY_LOG_SESSION_TIMEOUT } from 'gatsby-env-variables'
import { RootState } from 'store/rootReducer'
import getDateByPresetTimeRange from 'utils/getDateByPresetTimeRange'
import { toASCII } from 'utils/punycode'
import { QueriesFilterMenuType } from 'components/Dashboard/Analytics/ActivityLog/FiltersContainer'
import { ActionTrigger } from 'store/api/activityLog/activityLog.interface'
import type { ActivityLogParams, HistoricalQueriesResponse } from 'store/api/activityLog'
import { activityLogApi } from 'store/api/activityLog'
import moment from 'moment'

export type QueryFilters = {
  [key in QueriesFilterMenuType]?: string
} & {
  deviceId?: string
  clientId?: string
  search?: string
  endTs: number
  startTs: number
  page?: number
  pageSize?: number
}

export const buildActivityLogParams = (
  queryFilters: QueryFilters,
  presetTimeRange: PresetTimeRange,
  region?: string,
): ActivityLogParams => {
  const params: ActivityLogParams = {
    region,
  } as ActivityLogParams

  // Access all properties directly - enum values ARE the property names
  const { deviceId, clientId, rrType, statusCode, protocol, search, action, startTs, endTs } =
    queryFilters

  if (presetTimeRange !== PresetTimeRange.CUSTOM) {
    params.endTime = new Date().toISOString()
    params.startTime = (getDateByPresetTimeRange(presetTimeRange) || new Date()).toISOString()
  } else {
    params.startTime = moment(startTs).toISOString()
    params.endTime = moment(endTs).toISOString()
  }

  if (deviceId) {
    params.endpointId = deviceId
  }
  if (clientId) {
    params.clientId = clientId
  }
  if (rrType) {
    params.rrType = rrType
  }
  if (statusCode && statusCode !== 'all') {
    params.statusCode = Number(statusCode)
  }
  if (protocol && protocol !== 'all') {
    params.protocol = [protocol]
  }
  if (search) {
    params.searchQuestion = toASCII(search)
  }

  if (action != null && action !== 'all') {
    params.action = Number(action)
  }

  if (queryFilters.filters) {
    params.trigger = ActionTrigger.FILTER
    params.triggerValue = queryFilters.filters
  } else if (queryFilters.services) {
    params.trigger = ActionTrigger.SERVICE
    params.triggerValue = queryFilters.services
  }

  if (queryFilters.locations) {
    params.spoofTarget = queryFilters.locations
  }

  if (queryFilters.page) {
    params.page = queryFilters.page
  }
  if (queryFilters.pageSize) {
    params.pageSize = queryFilters.pageSize
  }

  return params
}

type ActivityLogState = {
  isEventSourceOpen: boolean
  meta?: HistoricalQueriesResponse['meta']
  logs: (HistoricalQueriesResponse['queries'][number] & { id: string })[]
  queryFilters: QueryFilters
  selectedPresetTimeRange: PresetTimeRange
  resetEventSource?: () => void
}

const initialState: ActivityLogState = {
  isEventSourceOpen: false,
  logs: [],
  queryFilters: {
    [QueriesFilterMenuType.ENDPOINT]: '',
    [QueriesFilterMenuType.CLIENT]: '',
    [QueriesFilterMenuType.RTYPE]: '',
    [QueriesFilterMenuType.RCODE]: '',
    [QueriesFilterMenuType.FILTERS]: '',
    [QueriesFilterMenuType.SERVICES]: '',
    [QueriesFilterMenuType.LOCATIONS]: '',
    [QueriesFilterMenuType.ACTION]: 'all',
    [QueriesFilterMenuType.PROTOCOL]: 'all',
    endTs: Date.now(),
    startTs: Date.now() - 1000 * 60 * 60 * 24,
  },
  selectedPresetTimeRange: PresetTimeRange.LAST_DAY,
}

export const startSessionTimer = createAsyncThunk(
  'activityLog/startSessionTimer',
  async (_, { dispatch, getState }) => {
    await new Promise(resolve => setTimeout(resolve, ACTIVITY_LOG_SESSION_TIMEOUT))
    const state = getState() as RootState
    const { region = '' } = state.session
    const presetTimeRange = state.activityLog.selectedPresetTimeRange
    const queryFilters = (getState() as RootState)?.activityLog.queryFilters
    const params = buildActivityLogParams(queryFilters, presetTimeRange, region)

    dispatch(endActivityLogSession())
    dispatch(activityLogApi.endpoints.getActivityLog.initiate(params))
  },
)

export const activityLogSlice = createSlice({
  name: 'activityLog',
  initialState: { ...initialState, ...initialResponseState },
  reducers: {
    addQuery: (state, action: PayloadAction<HistoricalQueriesResponse['queries'][number]>) => {
      state.logs.unshift({ id: uniqueId(), ...action.payload })
    },
    startActivityLogSession: state => {
      state.meta = undefined
      state.logs = []
      state.isEventSourceOpen = true
    },
    endActivityLogSession: state => {
      state.isEventSourceOpen = false
      state.resetEventSource?.()
      state.resetEventSource = undefined
      state.logs = []
    },
    deleteLogs: state => {
      state.logs = []
    },
    setQueryFilters(state, { payload }: PayloadAction<Partial<QueryFilters>>): void {
      state.queryFilters = { ...state.queryFilters, ...payload, page: payload.page || 0 }
    },
    resetFilterParameters(state): void {
      const { endTs, startTs } = state.queryFilters
      state.queryFilters = { ...initialState.queryFilters, endTs, startTs }
    },
    updateEventSourceHandler(state, { payload }: PayloadAction<() => void | undefined>): void {
      state.resetEventSource = payload
    },
    setSelectedPresetTimeRange(state, { payload }: PayloadAction<PresetTimeRange>): void {
      state.selectedPresetTimeRange = payload
    },
  },
  extraReducers: builder => {
    builder.addMatcher(activityLogApi.endpoints.getActivityLog.matchFulfilled, (state, action) => {
      state.logs = action.payload.queries.map(query => ({ ...query, id: uniqueId() }))
      state.meta = action.payload.meta
    })
  },
})

export const {
  addQuery,
  deleteLogs,
  startActivityLogSession,
  endActivityLogSession,
  setQueryFilters,
  resetFilterParameters,
  setSelectedPresetTimeRange,
  updateEventSourceHandler,
} = activityLogSlice.actions

export default activityLogSlice
